/* eslint-disable no-console */
import { useMemo, useCallback } from 'react';
import {
  ProjectAccessRoleEnum,
  SubscriptionProjectAccess,
} from '../../../server/graphql/__generated.types';
import {
  useApolloCacheUtilities,
  getFragmentName,
} from '../../util/apolloUtils';
import {
  ProjectAccessSubscriptionFragment,
  ProjectAccessSubscriptionFragmentDoc,
  TeamMemberProjectAccessFragment,
  TeamMemberProjectAccessFragmentDoc,
  ProjectAccessRoleFragment,
  ProjectAccessRoleFragmentDoc,
  ViewerProjectAccessFragment,
  ViewerProjectAccessFragmentDoc,
  TeamWideProjectAccessFragment,
  TeamWideProjectAccessFragmentDoc,
  TeamMemberFragment,
  TeamMemberFragmentDoc,
} from '../fragments.generated';
import { useUserProjectLazyQuery } from './operation.generated';

type TeamMemberAccess = TeamMemberProjectAccessFragment['teamMemberAccess'];

const logger = (enable: boolean): Pick<Console, 'info' | 'error'> => {
  return enable
    ? { info: console.info, error: console.error }
    : { info: () => {}, error: () => {} };
};

const log = logger(false);

const useProjectAccessCacheUtilities = () => {
  const { client, identityInCache } = useApolloCacheUtilities();

  const [fetchProject] = useUserProjectLazyQuery({
    fetchPolicy: 'network-only',
  });

  const readProjectAccessFragment = useCallback(
    // id is project.id \t viewer.id \t team.id (if applicable, else null)
    (id: string) => {
      const fragmentName = getFragmentName(
        ProjectAccessSubscriptionFragmentDoc
      );
      log.info(`readProjectAccessFragment: ${id}`);
      return client.readFragment<ProjectAccessSubscriptionFragment>({
        id: identityInCache('ProjectAccess', id),
        fragment: ProjectAccessSubscriptionFragmentDoc,
        fragmentName,
      });
    },
    [client, identityInCache]
  );

  const readProjectAccessRoleFragment = useCallback(
    (role: ProjectAccessRoleEnum) => {
      const fragmentName = getFragmentName(ProjectAccessRoleFragmentDoc);
      log.info(`readProjectAccessRoleFragment: ${role}`);
      return client.readFragment<ProjectAccessRoleFragment>({
        id: identityInCache('ProjectAccessRole', role),
        fragment: ProjectAccessRoleFragmentDoc,
        fragmentName,
      });
    },
    [client, identityInCache]
  );

  const readTeamMemberFragment = useCallback(
    (userId: string) => {
      log.info(`readTeamMemberFragment for userId: `, userId);
      return client.readFragment<TeamMemberFragment>({
        id: identityInCache('TeamMember', userId),
        fragment: TeamMemberFragmentDoc,
        fragmentName: getFragmentName(TeamMemberFragmentDoc),
      });
    },
    [client, identityInCache]
  );

  const readTeamMemberAccessFragment = useCallback(
    (projectAccessId: string) => {
      log.info(
        `readTeamMemberAccessFragment from projectAccessId: `,
        projectAccessId
      );
      return client.readFragment<TeamMemberProjectAccessFragment>({
        id: identityInCache('ProjectAccess', projectAccessId),
        fragment: TeamMemberProjectAccessFragmentDoc,
        fragmentName: getFragmentName(TeamMemberProjectAccessFragmentDoc),
      });
    },
    [client, identityInCache]
  );

  const writeViewerAccessFragment = useCallback(
    (
      projectAccessId: string,
      newViewerAccess: ViewerProjectAccessFragment['viewerAccessRole']
    ) => {
      log.info(
        `writeViewerAccessFragment from projectAccessId: `,
        projectAccessId
      );
      return client.writeFragment<ViewerProjectAccessFragment>({
        id: identityInCache('ProjectAccess', projectAccessId),
        fragment: ViewerProjectAccessFragmentDoc,
        data: { viewerAccessRole: newViewerAccess },
      });
    },
    [client, identityInCache]
  );

  const writeTeamWideAccessFragment = useCallback(
    (
      projectAccessId: string,
      newTeamWideAccess: TeamWideProjectAccessFragment['teamWideAccessRole']
    ) => {
      log.info(
        `writeTeamWideAccessFragment from projectAccessId: `,
        projectAccessId
      );
      return client.writeFragment<TeamWideProjectAccessFragment>({
        id: identityInCache('ProjectAccess', projectAccessId),
        fragment: TeamWideProjectAccessFragmentDoc,
        data: { teamWideAccessRole: newTeamWideAccess },
      });
    },
    [client, identityInCache]
  );

  const writeTeamMemberAccessFragment = useCallback(
    (
      projectAccessId: string,
      newTeamMemberAccess: TeamMemberProjectAccessFragment['teamMemberAccess']
    ) => {
      log.info(
        `writeTeamMemberAccessFragment from projectAccessId: `,
        projectAccessId
      );
      return client.writeFragment<TeamMemberProjectAccessFragment>({
        id: identityInCache('ProjectAccess', projectAccessId),
        fragment: TeamMemberProjectAccessFragmentDoc,
        data: { teamMemberAccess: newTeamMemberAccess },
      });
    },
    [client, identityInCache]
  );

  const projectAccessInCacheById = useCallback(
    (id: string) => {
      log.info(`projectAccessInCacheById: ${id}`);
      const projectAccess = readProjectAccessFragment(id);
      return projectAccess || undefined;
    },
    [readProjectAccessFragment]
  );

  const updateProjectAccessInCache = useCallback(
    (
      projectAccessEntity: SubscriptionProjectAccess,
      viewerId: string,
      projectAccessId: string
    ) => {
      const { projectId, userId, teamId, role } = projectAccessEntity;
      const exists = projectAccessInCacheById(projectAccessId);
      if (!exists) {
        log.error(
          `updateProjectAccessInCache: projectAccessId: ${projectAccessId} does not exist`
        );
        return;
      }
      if (!role) {
        // assumption is that update should only be called when there is a role, else delete called
        return;
      }
      // check to verify that cache has necessary permissions for incoming role
      const cachedRoleFragment = readProjectAccessRoleFragment(role);
      if (!cachedRoleFragment) {
        // if the cache does not have the necessary permissions for the incoming role
        // then need to query project because else label / permissions missing
        fetchProject({ variables: { inputs: { projectId } } });
        return;
      }
      if (userId === viewerId) {
        // update viewerAccessRole
        log.info(
          `updateViewerAccessRoleFromCache: projectAccessId ${projectAccessId}`
        );
        writeViewerAccessFragment(projectAccessId, cachedRoleFragment);
      }
      if (teamId && !userId) {
        // update teamWideAccessRole
        log.info(
          `updateTeamWideAccessRoleFromCache: projectAccessId ${projectAccessId}`
        );
        writeTeamWideAccessFragment(projectAccessId, cachedRoleFragment);
      } else if (teamId && userId) {
        // update teamMemberAccess
        const teamMemberAccess = readTeamMemberAccessFragment(projectAccessId);
        if (teamMemberAccess?.teamMemberAccess) {
          const newTeamMemberAccess = teamMemberAccess.teamMemberAccess?.reduce<
            NonNullable<TeamMemberAccess>
          >((acc, access) => {
            if (access.teamMember.id !== userId) {
              acc.push(access);
            } else {
              const { projectAccessRole, ...rest } = access;
              acc.push({ ...rest, projectAccessRole: cachedRoleFragment });
            }
            return acc;
          }, []);
          writeTeamMemberAccessFragment(projectAccessId, newTeamMemberAccess);
        }
      }
    },
    [
      fetchProject,
      projectAccessInCacheById,
      readProjectAccessRoleFragment,
      readTeamMemberAccessFragment,
      writeTeamMemberAccessFragment,
      writeTeamWideAccessFragment,
      writeViewerAccessFragment,
    ]
  );

  const createProjectAccessInCache = useCallback(
    (
      projectAccessEntity: SubscriptionProjectAccess,
      viewerId: string,
      projectAccessId: string
    ) => {
      const { projectId, userId, teamId, role } = projectAccessEntity;
      const exists = projectAccessInCacheById(projectAccessId);
      if (!exists) {
        log.error(
          `createProjectAccessInCache: projectAccessId: ${projectAccessId} does not exist`
        );
        return;
      }
      if (!role) {
        // assumption is that create should only be called when there is a role
        return;
      }
      // check to verify that cache has necessary permissions for incoming role
      const cachedRoleFragment = readProjectAccessRoleFragment(role);
      if (!cachedRoleFragment) {
        // if the cache does not have the necessary permissions for the incoming role
        // then need to query project because else label / permissions missing
        fetchProject({ variables: { inputs: { projectId } } });
        return;
      }
      if (userId === viewerId) {
        // create viewerAccessRole
        log.info(
          `createViewerAccessRoleFromCache: projectAccessId ${projectAccessId}`
        );
        writeViewerAccessFragment(projectAccessId, cachedRoleFragment);
      }
      if (teamId && !userId) {
        // create teamWideAccessRole
        log.info(
          `createTeamWideAccessRoleFromCache: projectAccessId ${projectAccessId}`
        );
        writeTeamWideAccessFragment(projectAccessId, cachedRoleFragment);
      } else if (teamId && userId) {
        // create teamMemberAccess
        const teamMemberAccess = readTeamMemberAccessFragment(projectAccessId);
        if (teamMemberAccess?.teamMemberAccess) {
          const specificTeamMember = readTeamMemberFragment(userId);
          if (!specificTeamMember) {
            fetchProject({ variables: { inputs: { projectId } } });
            return;
          }
          const newTeamMemberAccess = teamMemberAccess.teamMemberAccess?.concat(
            {
              projectAccessRole: cachedRoleFragment,
              __typename: 'TeamMemberAccess',
              id: '',
              teamMember: specificTeamMember,
            }
          );
          writeTeamMemberAccessFragment(projectAccessId, newTeamMemberAccess);
        }
      }
    },
    [
      fetchProject,
      projectAccessInCacheById,
      readProjectAccessRoleFragment,
      readTeamMemberAccessFragment,
      readTeamMemberFragment,
      writeTeamMemberAccessFragment,
      writeTeamWideAccessFragment,
      writeViewerAccessFragment,
    ]
  );

  const removeProjectAccessFromCache = useCallback(
    (
      projectAccessEntity: SubscriptionProjectAccess,
      viewerId: string,
      projectAccessId: string
    ) => {
      const { userId, teamId } = projectAccessEntity;
      const exists = projectAccessInCacheById(projectAccessId);
      if (!exists) {
        log.error(
          `removeProjectAccessFromCache: projectAccessId: ${projectAccessId} does not exist`
        );
        return;
      }

      if (userId === viewerId) {
        // remove viewerAccessRole
        log.info(
          `removeViewerAccessRoleFromCache: projectAccessId ${projectAccessId}`
        );
        client.cache.evict({
          id: identityInCache('ProjectAccess', projectAccessId),
          fieldName: 'viewerAccessRole',
        });
      }

      if (teamId && !userId) {
        // remove teamWideAccessRole
        log.info(
          `removeTeamWideAccessRoleFromCache: projectAccessId ${projectAccessId}`
        );
        client.cache.evict({
          id: identityInCache('ProjectAccess', projectAccessId),
          fieldName: 'teamWideAccessRole',
        });
      } else if (teamId && userId) {
        // remove teamMemberAccess
        // viewer access is also stored in this array; same array for team/shared projects
        const teamMemberAccess = readTeamMemberAccessFragment(projectAccessId);
        if (teamMemberAccess?.teamMemberAccess) {
          const newTeamMemberAccess = teamMemberAccess.teamMemberAccess?.reduce<
            NonNullable<TeamMemberAccess>
          >((acc, access) => {
            if (access.teamMember.id !== userId) {
              acc.push(access);
            }
            return acc;
          }, []);
          writeTeamMemberAccessFragment(projectAccessId, newTeamMemberAccess);
        }
      }
      client.cache.gc();
    },
    [
      client.cache,
      identityInCache,
      projectAccessInCacheById,
      readTeamMemberAccessFragment,
      writeTeamMemberAccessFragment,
    ]
  );

  return useMemo(() => {
    return {
      readProjectAccessFragment,
      readTeamMemberFragment,
      updateProjectAccessInCache,
      removeProjectAccessFromCache,
      createProjectAccessInCache,
    };
  }, [
    readProjectAccessFragment,
    readTeamMemberFragment,
    updateProjectAccessInCache,
    removeProjectAccessFromCache,
    createProjectAccessInCache,
  ]);
};

export default useProjectAccessCacheUtilities;
